import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, RefreshCw, Smartphone, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';

const OTPVerification = ({ onBack }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes countdown
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef([]);
  const { otpSession, verifyOtp, resendOtp, isVerifyingOtp } = useAuth();

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  // Focus on first input when component mounts
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are filled
    if (newOtp.every(digit => digit !== '') && newOtp.join('').length === 6) {
      handleSubmit(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 6);
        if (digits.length > 0) {
          const newOtp = [...otp];
          for (let i = 0; i < Math.min(digits.length, 6); i++) {
            newOtp[i] = digits[i];
          }
          setOtp(newOtp);
          
          // Focus on next empty input or last input
          const nextIndex = Math.min(digits.length, 5);
          inputRefs.current[nextIndex]?.focus();
          
          // Auto-submit if complete
          if (digits.length === 6) {
            handleSubmit(digits);
          }
        }
      });
    }
  };

  const handleSubmit = async (otpCode = null) => {
    const codeToSubmit = otpCode || otp.join('');
    if (codeToSubmit.length !== 6) {
      return;
    }

    try {
      await verifyOtp(codeToSubmit);
    } catch (error) {
      // Error handling is done in the auth context
      // Clear OTP inputs on error
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  };

  const handleResendOtp = async () => {
    setIsResending(true);
    try {
      await resendOtp();
      setTimeLeft(300); // Reset timer
      setOtp(['', '', '', '', '', '']); // Clear current OTP
      inputRefs.current[0]?.focus();
    } catch (error) {
      // Error handling is done in the auth context
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getMaskedPhone = () => {
    // Demo phone number
    return '***-***-1234';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary-100 mb-4">
            <Shield className="h-8 w-8 text-primary-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            {otpSession?.isDemoAccount ? 'Demo Account Verification' : 'Verify Your Identity'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {otpSession?.isDemoAccount 
              ? 'Demo mode: Enter 000000 to continue'
              : 'We\'ve sent a verification code to your email address'
            }
          </p>
        </div>

        {/* OTP Input Form */}
        <div className="bg-white shadow-lg rounded-lg p-8">
          <div className="space-y-6">
            {/* OTP Input Fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                Please enter the 6-digit verification code
              </label>
              <div className="flex justify-center space-x-3">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:ring-primary-500 focus:outline-none transition-colors"
                    disabled={isVerifyingOtp}
                  />
                ))}
              </div>
            </div>

            {/* Demo Hint */}
            {otpSession?.isDemoAccount ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <Smartphone className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-blue-800 font-medium">Demo Account</p>
                      <p className="text-xs text-blue-600 mt-1">
                        Enter <strong>000000</strong> to complete verification
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newOtp = ['0', '0', '0', '0', '0', '0'];
                      setOtp(newOtp);
                      // Auto-submit
                      setTimeout(() => handleSubmit('000000'), 100);
                    }}
                    className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors"
                  >
                    Auto-fill
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-start">
                  <Smartphone className="h-5 w-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-green-800 font-medium">Check Your Email</p>
                    <p className="text-xs text-green-600 mt-1">
                      Enter the 6-digit code sent to your email address
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Timer and Resend */}
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-3">
                Code expires in: {formatTime(timeLeft)}
              </p>
              
              {timeLeft > 0 ? (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={isResending || timeLeft > 240} // Can resend after 1 minute
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                >
                                     {isResending ? (
                     <>
                       <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                       Resending...
                     </>
                   ) : timeLeft > 240 ? (
                     `Resend available in ${formatTime(240 - (300 - timeLeft))}`
                   ) : (
                     <>
                       <RefreshCw className="w-4 h-4 mr-2" />
                       Resend Code
                     </>
                   )}
                </button>
              ) : (
                                 <div className="text-red-600 text-sm">
                   Code expired, please login again
                 </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="button"
              onClick={() => handleSubmit()}
              disabled={isVerifyingOtp || otp.some(digit => digit === '') || timeLeft === 0}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
                             {isVerifyingOtp ? (
                 <>
                   <LoadingSpinner size="sm" />
                   <span className="ml-2">Verifying...</span>
                 </>
               ) : (
                 'Verify & Login'
               )}
            </button>

            {/* Back Button */}
            <button
              type="button"
              onClick={onBack}
              disabled={isVerifyingOtp}
              className="w-full flex justify-center items-center py-2 px-4 text-sm font-medium text-gray-600 hover:text-gray-800 disabled:text-gray-400 transition-colors"
            >
                             <ArrowLeft className="w-4 h-4 mr-2" />
               Back to Login
            </button>
          </div>
        </div>

        {/* User Info */}
        {otpSession && (
                     <div className="text-center text-sm text-gray-500">
             Verifying identity for <span className="font-medium">{otpSession.user.email}</span>
           </div>
        )}
      </div>
    </div>
  );
};

export default OTPVerification; 